/**
 * Sentry Error Tracking for FeedbackHub Web App
 *
 * Provides error tracking, user context, and browser tracing.
 * Gracefully degrades when DSN is not configured.
 */

import * as Sentry from '@sentry/react';

let isInitialized = false;

// Error messages that should not be reported to Sentry
const IGNORED_ERROR_PATTERNS = [
  // Auth errors are expected
  /authentication required/i,
  /unauthorized/i,
  /401/,
  // Network errors when offline
  /network error/i,
  /failed to fetch/i,
  // CSRF errors handled by retry logic
  /csrf/i,
];

/**
 * Initialize Sentry for the web app.
 * Should be called before ReactDOM.render().
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.log('Sentry DSN not configured, error tracking disabled');
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE || 'development',
    tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '1.0'),
    // Enable browser tracing
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Capture 10% of sessions for replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Filter out expected errors
    beforeSend(event, hint) {
      const error = hint.originalException;
      if (error instanceof Error) {
        const message = error.message;
        if (IGNORED_ERROR_PATTERNS.some(pattern => pattern.test(message))) {
          return null;
        }
      }
      return event;
    },
  });

  isInitialized = true;
  console.log('Sentry initialized');
}

/**
 * Capture an exception to Sentry with optional context.
 * Returns the Sentry event ID for reference.
 */
export function captureException(
  error: Error,
  context?: Record<string, unknown>
): string | undefined {
  if (!isInitialized) {
    return undefined;
  }

  // Don't capture ignored errors
  if (IGNORED_ERROR_PATTERNS.some(pattern => pattern.test(error.message))) {
    return undefined;
  }

  return Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Set user context for Sentry.
 * Call this after authentication to associate errors with users.
 */
export function setUser(user: { id: string; isAdmin?: boolean } | null): void {
  if (!isInitialized) {
    return;
  }

  if (user) {
    Sentry.setUser({
      id: user.id,
      // Don't include email for privacy
    });
    Sentry.setTag('isAdmin', user.isAdmin ? 'true' : 'false');
  } else {
    Sentry.setUser(null);
    Sentry.setTag('isAdmin', undefined);
  }
}

/**
 * Show Sentry's feedback dialog for users to report issues.
 */
export function showReportDialog(eventId?: string): void {
  if (!isInitialized) {
    return;
  }

  Sentry.showReportDialog({
    eventId,
    title: 'Report an Issue',
    subtitle: 'Help us fix this problem by describing what happened.',
    subtitle2: '',
    labelName: 'Name',
    labelEmail: 'Email',
    labelComments: 'What happened?',
    labelClose: 'Close',
    labelSubmit: 'Submit Report',
    successMessage: 'Thank you for your feedback!',
  });
}

/**
 * Get the last event ID captured by Sentry.
 */
export function getLastEventId(): string | undefined {
  if (!isInitialized) {
    return undefined;
  }
  return Sentry.lastEventId();
}

export { Sentry };
